import { batch } from 'react-redux';
import type { NavigateFunction } from 'react-router-dom';
import { saveLocation } from '../../../apis/locationAPI';
import {
  cancelWorkAlonePersonalAlarm,
  createWorkAlonePersonalAlarm,
  getWorkAlonePersonalAlarmById,
  getWorkAlonePersonalAlarmStatus,
  setOffWorkAlonePersonalAlarm,
  updateWorkAlonePersonalAlarm,
} from '../../../apis/loneWorkerTimerAPI';
import type { GetUserQuery } from '../../../apis/userAPI';
import { getAllUsers } from '../../../apis/userAPI';
import type { AppThunk } from '../../../store';
import { fetchTenant } from '../../Organizations/OrganizationsSlice/actionCreators';
import { fetchChats, fetchTotalUnread } from '../../ChatsList/chatListSlice/actionCreators';
import { setGroupMembers } from '../../CreateMessage/createMessageSlice';
import type { GroupMember, Location } from '../../GroupDetail/groupDetailSlice/types';
import type { Group } from '../../GroupsList/groupsSlice/types';
import {
  setAlarmData,
  setGroups,
  setGroupsLoading,
  setGroupsMembers,
  setIsDeactivated,
  setIsError,
  setIsLoading,
  setIsLocationDenied,
  setMessageText,
  setHasPreAlarmNotifications,
  setSelectedGroups,
  setSelectedUsers,
  setTime,
  setUsers,
  setUsersLoading,
} from './index';
import type { LoneWorkerPersonalAlarmCreateBody, UpdateTimerData } from './types';
import type { SelectListUser } from '../../CreateMessage/createMessageSlice/types';
import type { GetGroupsQuery, GroupsData } from '../../../api/groups';
import { localStorageRemove } from '../../../utils/storage';
import { fetchAllGroupMembers, getGroups } from '../../../api/groups';
import { isLwtSenderFeaturesEnabled } from '../../../utils/features';
import { timespanToSeconds } from '../helpers';

export const fetchGroups =
  (query?: Omit<GetGroupsQuery, 'function[in]'>): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(setGroupsLoading(true));
      let tenant = getState().organizations.tenant;
      if (!tenant) {
        await dispatch(fetchTenant());
        tenant = getState().organizations.tenant;
        if (!tenant) {
          throw new Error('Missing tenant');
        }
      }

      const currentGroups = getState().loneWorkerTimer.groups || [];
      let allGroups = [...currentGroups];
      const currentGroupsIdsList = [...currentGroups].map(group => group.id);

      const result = await getGroups(tenant.id, { ...query, 'function[in]': 'lone_worker_timer' });
      if (result.error) {
        throw new Error();
      }

      if (query?.offset) {
        const filteredNewGroups: GroupsData[] = result.data.data.filter(
          group => !currentGroupsIdsList.includes(group.id)
        );
        allGroups = [...allGroups, ...filteredNewGroups];
      } else {
        allGroups = [...result.data.data];
      }
      batch(() => {
        dispatch(setGroups(allGroups));
        dispatch(setGroupsLoading(false));
      });
    } catch (error) {
      dispatch(setGroupsLoading(false));
    }
  };

export const fetchUsers =
  (query?: GetUserQuery): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(setUsersLoading(true));
      const currentUsers = getState().loneWorkerTimer.users || [];
      let allUsers = [...currentUsers];
      const newUsers = await getAllUsers(query);
      if (query?.skip) {
        const filteredNewUsers = [...newUsers].filter(
          user => !allUsers.find(u => u.id === user.id)
        );
        allUsers = [...allUsers, ...filteredNewUsers];
      } else {
        allUsers = [...newUsers];
      }
      batch(() => {
        dispatch(setUsers(allUsers));
        dispatch(setUsersLoading(false));
      });
    } catch (error) {
      console.log('error log ', error);
      batch(() => {
        dispatch(setUsersLoading(false));
        dispatch(setIsError(`${error}`));
      });
    }
  };

const getGroupMembersList = async (
  selectedGroup: Group,
  currentUserId: number | undefined,
  tenantId: number
) => {
  try {
    const members: GroupMember[] = [];

    const usersInGroup = await fetchAllGroupMembers({ groupId: selectedGroup.id, tenantId });
    usersInGroup.forEach((member: GroupMember) => {
      if (member.userID !== currentUserId) {
        members.push({
          ...member,
          isSelected: true,
          groupID: member.groupID,
        });
      }
    });
    return members;
  } catch (error) {
    return [];
  }
};

export const loadGroupMembers =
  (
    selectedGroup: Group,
    currentUserId: number | undefined,
    onMembersLoad?: (members: GroupMember[], selectedGroup: Group) => void
  ): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(setIsLoading(true));

      let tenant = getState().organizations.tenant;
      if (!tenant) {
        await dispatch(fetchTenant());
        tenant = getState().organizations.tenant;
        if (!tenant) {
          throw new Error('Missing tenant');
        }
      }

      const members: GroupMember[] = await getGroupMembersList(
        selectedGroup,
        currentUserId,
        tenant.id
      );
      if (onMembersLoad) {
        onMembersLoad(members, selectedGroup);
      }
      batch(() => {
        dispatch(setGroupsMembers(members));
        dispatch(setIsLoading(false));
      });
    } catch (error) {
      batch(() => {
        dispatch(setIsLoading(false));
        dispatch(setIsError(`${error}`));
      });
    }
  };

export const clearCreateData = (): AppThunk => async (dispatch, getState) => {
  try {
    const authUser = getState().user.user;
    dispatch(setIsLoading(true));
    if (authUser) {
      dispatch(
        setSelectedUsers([
          {
            id: authUser.id,
            displayName: authUser.name ?? '',
            groupIds: null,
            isSelected: true,
          } as SelectListUser,
        ])
      );
    }
    batch(() => {
      dispatch(setGroups([]));
      dispatch(setUsers([]));
      dispatch(setSelectedGroups([]));
      dispatch(setGroupMembers([]));
      dispatch(setTime({ hours: 0, minutes: 0 }));
      dispatch(setMessageText(''));
      dispatch(setHasPreAlarmNotifications(true));
      dispatch(setIsLoading(false));
      dispatch(setIsLocationDenied(false));
    });
  } catch (error) {
    console.log(error);
    batch(() => {
      dispatch(setIsError(`${error}`));
      dispatch(setIsLoading(false));
    });
  }
};

export const createAlarm =
  (
    alarm: LoneWorkerPersonalAlarmCreateBody,
    location: Location | null,
    navigate: NavigateFunction
  ): AppThunk =>
  async dispatch => {
    try {
      dispatch(setIsLoading(true));
      if (location) {
        const locationResult = await saveLocation(location);
        alarm.locationId = locationResult?.id;
      }
      const alarmData = await createWorkAlonePersonalAlarm(alarm);
      batch(() => {
        dispatch(setAlarmData(alarmData));
        dispatch(setIsLoading(false));
      });
      clearCreateData();
      navigate(`/aloneWorker/timer`);
    } catch (error) {
      batch(() => {
        dispatch(setIsError(`${error}`));
        dispatch(setIsLoading(false));
      });
    }
  };

export const fetchAlarmData =
  (navigate: NavigateFunction, fromMainMenu: boolean): AppThunk =>
  async dispatch => {
    try {
      dispatch(setIsLoading(true));
      const alarmData = await getWorkAlonePersonalAlarmStatus();

      if (fromMainMenu) {
        if (!isLwtSenderFeaturesEnabled()) {
          if (alarmData && alarmData.MessageId) {
            localStorageRemove('isLoneWorkTimerActive');
            dispatch(fetchTotalUnread());
            await cancelWorkAlonePersonalAlarm();
          }
        } else {
          if (alarmData && timespanToSeconds(alarmData.DispatchDelay) <= 0) {
            navigate('/aloneWorker/timer');
          }
        }
      }
      batch(() => {
        dispatch(setAlarmData(alarmData));
        dispatch(setIsLoading(false));
      });
    } catch (error) {
      batch(() => {
        dispatch(setAlarmData(null));
        dispatch(setIsError(`${error}`));
        dispatch(setIsLoading(false));
      });
      localStorageRemove('isLoneWorkTimerActive');

      if (!fromMainMenu) navigate('/dashboard');
    }
  };

export const fetchFinishedAlarmData =
  (navigate: NavigateFunction): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(setIsLoading(true));
      const currentAlarmData = getState().loneWorkerTimer.alarmData;
      const alarmData = await getWorkAlonePersonalAlarmById(currentAlarmData?.Id);
      if (alarmData) {
        batch(() => {
          dispatch(setAlarmData(alarmData));
          dispatch(setIsLoading(false));
        });
      } else {
        batch(() => {
          dispatch(setAlarmData(null));
          dispatch(setIsError(`Alarm does not exist`));
          dispatch(setIsLoading(false));
        });
        navigate('dashboard');
      }
    } catch (error) {
      console.log('Meets error', error);
      batch(() => {
        dispatch(setAlarmData(null));
        dispatch(setIsError(`${error}`));
        dispatch(setIsLoading(false));
      });
      navigate('dashboard');
    }
  };

export class LwtDispatchTimeExceeds24h extends Error {}

export const updateTimer =
  (updateTimerData: UpdateTimerData): AppThunk<Promise<void>> =>
  async dispatch => {
    try {
      dispatch(setIsLoading(true));
      const updatedAlarm = await updateWorkAlonePersonalAlarm(updateTimerData, true).catch(
        error => {
          if (error?.Code === 'DISPATCH_TIME_EXCEEDS_24H') throw new LwtDispatchTimeExceeds24h();
          throw error;
        }
      );
      batch(() => {
        dispatch(setAlarmData(updatedAlarm));
        dispatch(setIsLoading(false));
      });
    } catch (error) {
      batch(() => {
        dispatch(setIsError(`${error}`));
        dispatch(setIsLoading(false));
      });
      if (error instanceof LwtDispatchTimeExceeds24h) {
        throw error;
      }
    }
  };

export const deactivateAlarm =
  (navigate: NavigateFunction, path: string): AppThunk =>
  async dispatch => {
    try {
      dispatch(setIsLoading(true));
      await cancelWorkAlonePersonalAlarm();
      navigate(path);
      batch(() => {
        dispatch(setAlarmData(null));
        dispatch(setIsDeactivated(true));
        dispatch(setIsLoading(false));
      });
    } catch (error) {
      batch(() => {
        dispatch(setIsError(`${error}`));
        dispatch(setIsLoading(false));
      });
    }
  };

class LwtNotFound extends Error {}
class LwtMessageMissing extends Error {}

export const goToAlarmChat =
  (
    navigate: NavigateFunction,
    /** @callback only to communicate errors via UI */
    onErrorFeedback: (errorTx: string) => void
  ): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(setIsLoading(true));
      const alarmResponse = await getWorkAlonePersonalAlarmById(
        getState().loneWorkerTimer.alarmData?.Id,
        true
      ).catch(error => {
        if (error?.Code === 'ENTITY_IS_NOT_FOUND') throw new LwtNotFound();
        throw error;
      });

      if (!alarmResponse?.MessageId) throw new LwtMessageMissing();

      dispatch(fetchChats({}));
      navigate(`/message/${alarmResponse?.MessageId}`);

      cancelWorkAlonePersonalAlarm();

      batch(() => {
        dispatch(setAlarmData(null));
        dispatch(setIsLoading(false));
      });
    } catch (error) {
      batch(() => {
        dispatch(setIsError(`${error}`));
        dispatch(setIsLoading(false));
      });
      if (error instanceof LwtNotFound) {
        dispatch(setAlarmData(null));
        navigate('dashboard');
      }

      onErrorFeedback(
        error instanceof LwtMessageMissing ? 'error_try_again' : 'general_network_error'
      );
    }
  };

export const setOffTimer =
  (navigate: NavigateFunction, onErrorFeedback: (errorTx: string) => void): AppThunk =>
  async dispatch => {
    try {
      dispatch(setIsLoading(true));
      await setOffWorkAlonePersonalAlarm();
      setTimeout(() => {
        localStorageRemove('isLoneWorkTimerActive');
        dispatch(goToAlarmChat(navigate, onErrorFeedback));
      }, 5000);
    } catch (error) {
      console.error(error);
      batch(() => {
        dispatch(setIsError(`${error}`));
        dispatch(setIsLoading(false));
      });
    }
  };
